import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ColorType,
  createChart,
  LineSeries,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type UTCTimestamp,
} from 'lightweight-charts';
import { hasManualTradingOrigin } from '@/config/api';
import { usePortfolioWs } from '@/hooks/usePortfolioWs';
import { PositionGroupHistory } from '@/pages/PositionGroupHistory';
import {
  getAccountDailySummaries,
  getDailyAccountPnl,
  listHistoryOrders,
  listHistoryTasks,
  listAccounts,
  type AccountDailySummaryView,
  type AccountInfoView,
  type AccountSummaryView,
  type DailyAccountPnlView,
  type HistoryOrderView,
  type TaskView,
  getAccountSummary,
} from '@/services/manualTradingApi';
import { useSearchParams } from 'react-router-dom';
import './AccountOverview.css';
import { FundTransferSection } from './FundTransferSection';

type AccountTab = 'overview' | 'records' | 'transfers';
type RecordTab = 'groups' | 'tasks' | 'orders';

const ACCOUNT_TABS: Array<{ key: AccountTab; label: string }> = [
  { key: 'overview', label: '总览' },
  { key: 'records', label: '历史记录' },
  { key: 'transfers', label: '资金划转' },
];

const ACCOUNT_TAB_KEYS = new Set<AccountTab>(ACCOUNT_TABS.map((tab) => tab.key));
/** Legacy tab query values that now map into the combined overview tab. */
const LEGACY_OVERVIEW_TABS = new Set(['assets', 'performance']);

const EXCHANGE_COLORS: Record<string, string> = {
  OKX: '#2563eb',
  Bybit: '#f97316',
  Binance: '#eab308',
  Bitget: '#06b6d4',
  Gateio: '#10b981',
  Hyperliquid: '#8b5cf6',
  Aster: '#ec4899',
  EdgeX: '#64748b',
  Lighter: '#14b8a6',
  Paradex: '#6366f1',
};

const MS_PER_DAY = 86_400_000;

function utcDayStartMs(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function formatUsd(v: number | null | undefined, digits = 2): string {
  if (v == null || Number.isNaN(v)) return '—';
  return v.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  });
}

function formatPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  const pct = v * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

function formatRate(v: number): string {
  if (Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function formatTs(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function formatCompactTs(ms: number | null | undefined): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 18 ? `${id.slice(0, 8)}…${id.slice(-6)}` : id;
}

function pnlClass(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v) || v === 0) return '';
  return v > 0 ? 'positive' : 'negative';
}

function exchangeColor(exchange: string): string {
  return EXCHANGE_COLORS[exchange] ?? '#7c3aed';
}

function monthLabel(year: number, month: number): string {
  return new Date(Date.UTC(year, month, 1)).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });
}

function toChartLineData(points: Array<{ t: number; v: number }>): LineData<UTCTimestamp>[] {
  const bySecond = new Map<number, number>();
  for (const point of points) {
    if (!Number.isFinite(point.t) || !Number.isFinite(point.v)) continue;
    bySecond.set(Math.floor(point.t / 1000), point.v);
  }
  return Array.from(bySecond.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([time, value]) => ({
      time: time as UTCTimestamp,
      value,
    }));
}

function EquityLineChart({
  title,
  points,
  height = 280,
  color = '#0f766e',
}: {
  title: string;
  points: Array<{ t: number; v: number }>;
  height?: number;
  color?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);
  const lineDataRef = useRef<LineData<UTCTimestamp>[]>([]);
  const lineData = useMemo(() => toChartLineData(points), [points]);
  lineDataRef.current = lineData;

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.replaceChildren();
    const chart = createChart(container, {
      width: container.clientWidth || 640,
      height,
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#374151',
      },
      grid: {
        vertLines: { color: '#f3f4f6' },
        horzLines: { color: '#f3f4f6' },
      },
      rightPriceScale: { borderColor: '#e5e7eb' },
      timeScale: { borderColor: '#e5e7eb', timeVisible: true, secondsVisible: false },
    });
    const series = chart.addSeries(LineSeries, {
      color,
      lineWidth: 2,
      priceFormat: { type: 'price', precision: 2, minMove: 0.01 },
    });

    chartRef.current = chart;
    seriesRef.current = series;
    series.setData(lineDataRef.current);
    if (lineDataRef.current.length > 0) {
      chart.timeScale().fitContent();
    }

    const ro = new ResizeObserver(() => {
      const w = container.clientWidth;
      if (w > 0) chart.applyOptions({ width: w });
    });
    ro.observe(container);

    requestAnimationFrame(() => {
      const w = container.clientWidth;
      if (w > 0) chart.applyOptions({ width: w });
    });

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [height, color]);

  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart) return;

    series.setData(lineData);
    if (lineData.length > 0) {
      chart.timeScale().fitContent();
    }
  }, [lineData]);

  return (
    <div className="account-overview-section">
      <h3>{title}</h3>
      {lineData.length === 0 ? (
        <p className="account-overview-empty">暂无曲线数据</p>
      ) : null}
      <div className="account-overview-chart-wrap" ref={containerRef} />
    </div>
  );
}

function PnlCalendar({
  year,
  month,
  daysByStart,
  selectedDayStart,
  onSelectDay,
  onPrevMonth,
  onNextMonth,
}: {
  year: number;
  month: number;
  daysByStart: Map<number, AccountDailySummaryView>;
  selectedDayStart: number | null;
  onSelectDay: (dayStartMs: number) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}) {
  const weekdays = ['一', '二', '三', '四', '五', '六', '日'];
  const firstDow = (new Date(Date.UTC(year, month, 1)).getUTCDay() + 6) % 7;
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const cells: Array<number | null> = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const maxAbsPnl = useMemo(() => {
    let max = 1;
    for (const day of daysByStart.values()) {
      max = Math.max(max, Math.abs(day.daily_pnl));
    }
    return max;
  }, [daysByStart]);

  return (
    <div className="account-overview-section">
      <div className="account-overview-calendar-head">
        <h3 style={{ margin: 0 }}>每日收益日历</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button type="button" onClick={onPrevMonth} aria-label="上个月">
            ‹
          </button>
          <span>{monthLabel(year, month)}</span>
          <button type="button" onClick={onNextMonth} aria-label="下个月">
            ›
          </button>
        </div>
      </div>
      <div className="account-overview-calendar-grid">
        {weekdays.map((w) => (
          <div key={w} className="account-overview-calendar-weekday">
            {w}
          </div>
        ))}
        {cells.map((day, idx) => {
          if (day == null) {
            return <div key={`empty-${idx}`} className="account-overview-calendar-cell empty" />;
          }
          const dayStart = Date.UTC(year, month, day);
          const summary = daysByStart.get(dayStart);
          const selected = selectedDayStart === dayStart;
          const intensity =
            summary && summary.daily_pnl !== 0
              ? Math.min(0.45, (Math.abs(summary.daily_pnl) / maxAbsPnl) * 0.45)
              : 0;
          const bg =
            summary == null
              ? '#fafafa'
              : summary.daily_pnl > 0
                ? `rgba(5, 150, 105, ${0.08 + intensity})`
                : summary.daily_pnl < 0
                  ? `rgba(220, 38, 38, ${0.08 + intensity})`
                  : '#fafafa';

          return (
            <div
              key={dayStart}
              className={`account-overview-calendar-cell${summary ? ' clickable' : ''}${selected ? ' selected' : ''}`}
              style={{ background: bg }}
              onClick={() => summary && onSelectDay(dayStart)}
              role={summary ? 'button' : undefined}
              tabIndex={summary ? 0 : undefined}
              onKeyDown={(e) => {
                if (summary && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelectDay(dayStart);
                }
              }}
            >
              <div className="account-overview-calendar-day">{day}</div>
              {summary ? (
                <>
                  <div className={`account-overview-calendar-pnl ${pnlClass(summary.daily_pnl)}`}>
                    {summary.daily_pnl >= 0 ? '+' : ''}
                    {formatUsd(summary.daily_pnl, 0)}
                  </div>
                  <div className="account-overview-calendar-equity">
                    {formatUsd(summary.close_usd, 0)}
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HistoryRecordsPanel() {
  const [recordTab, setRecordTab] = useState<RecordTab>('groups');
  const [symbol, setSymbol] = useState('');
  const [limit, setLimit] = useState(100);
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [orders, setOrders] = useState<HistoryOrderView[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);

  const loadExecutionRows = useCallback(async () => {
    if (recordTab === 'groups') return;
    setLoading(true);
    setError(null);
    try {
      const params = {
        symbol: symbol.trim() || undefined,
        limit: Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 100,
      };
      if (recordTab === 'tasks') {
        const rows = await listHistoryTasks(params);
        setTasks(rows);
      } else {
        const rows = await listHistoryOrders(params);
        setOrders(rows);
      }
      setLastOkAt(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [limit, recordTab, symbol]);

  useEffect(() => {
    void loadExecutionRows();
  }, [loadExecutionRows]);

  return (
    <section className="account-records-panel">
      <div className="account-records-head">
        <div>
          <h3>历史记录</h3>
          <p>按仓位组复盘盈亏，向下可追溯到 task 与 order 执行明细。</p>
        </div>
        <div className="account-record-tabs" role="tablist" aria-label="历史记录类型">
          <button
            type="button"
            className={recordTab === 'groups' ? 'active' : ''}
            onClick={() => setRecordTab('groups')}
          >
            仓位组
          </button>
          <button
            type="button"
            className={recordTab === 'tasks' ? 'active' : ''}
            onClick={() => setRecordTab('tasks')}
          >
            任务
          </button>
          <button
            type="button"
            className={recordTab === 'orders' ? 'active' : ''}
            onClick={() => setRecordTab('orders')}
          >
            订单
          </button>
        </div>
      </div>

      {recordTab === 'groups' ? (
        <PositionGroupHistory embedded />
      ) : (
        <>
          <div className="account-record-filters">
            <label>
              Symbol
              <input value={symbol} placeholder="全部" onChange={(e) => setSymbol(e.target.value)} />
            </label>
            <label>
              Limit
              <input
                type="number"
                min={1}
                max={500}
                value={limit}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  setLimit(Number.isFinite(next) && next > 0 ? Math.floor(next) : 100);
                }}
              />
            </label>
            <button type="button" disabled={loading} onClick={() => void loadExecutionRows()}>
              {loading ? '刷新中…' : '刷新'}
            </button>
            <span className="account-record-meta">
              {lastOkAt ? `更新于 ${new Date(lastOkAt).toLocaleTimeString('zh-CN', { hour12: false })}` : '等待数据'}
            </span>
          </div>

          {error ? <p className="account-overview-error">{error}</p> : null}

          <div className="account-record-table-wrap">
            {recordTab === 'tasks' ? (
              tasks.length === 0 && !loading && !error ? (
                <p className="account-overview-empty">暂无历史任务。</p>
              ) : (
                <table className="account-record-table">
                  <thead>
                    <tr>
                      <th>时间</th>
                      <th>Symbol</th>
                      <th>动作</th>
                      <th>状态</th>
                      <th>Long / Short</th>
                      <th className="num">Commit spread</th>
                      <th className="num">Fill spread</th>
                      <th>Task ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task) => (
                      <tr key={task.task_id}>
                        <td>{formatCompactTs(task.created_at)}</td>
                        <td className="strong">{task.symbol}</td>
                        <td>{task.action}</td>
                        <td>{task.status}</td>
                        <td>
                          {task.long_leg.exchange} / {task.short_leg.exchange}
                        </td>
                        <td className="num">{formatPct(task.commit_spread_ratio)}</td>
                        <td className="num">{formatPct(task.fill_spread_ratio)}</td>
                        <td className="mono" title={task.task_id}>
                          {shortId(task.task_id)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : orders.length === 0 && !loading && !error ? (
              <p className="account-overview-empty">暂无历史订单。</p>
            ) : (
              <table className="account-record-table">
                <thead>
                  <tr>
                    <th>时间</th>
                    <th>Symbol</th>
                    <th>交易所</th>
                    <th>Inst</th>
                    <th>动作</th>
                    <th>方向</th>
                    <th>状态</th>
                    <th className="num">名义</th>
                    <th className="num">成交价</th>
                    <th className="num">滑点</th>
                    <th>耗时</th>
                    <th>Order ID</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={`${order.client_order_id}-${order.updated_at_ms}`}>
                      <td>{formatCompactTs(order.updated_at_ms || order.commit_time_ms)}</td>
                      <td className="strong">{order.symbol}</td>
                      <td>{order.exchange}</td>
                      <td className="mono">{order.inst_id}</td>
                      <td>{order.action}</td>
                      <td>{order.position_side}</td>
                      <td>{order.status}</td>
                      <td className="num">{formatUsd(order.notional_usd, 0)}</td>
                      <td className="num">{order.filled_price == null ? '—' : order.filled_price.toFixed(6)}</td>
                      <td className={`num ${pnlClass(order.slippage_ratio == null ? 0 : -order.slippage_ratio)}`}>
                        {formatPct(order.slippage_ratio)}
                      </td>
                      <td>{formatDuration(order.fill_duration_ms)}</td>
                      <td className="mono" title={order.client_order_id}>
                        {shortId(order.client_order_id)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </section>
  );
}

export function AccountOverview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: AccountTab =
    tabParam && ACCOUNT_TAB_KEYS.has(tabParam as AccountTab) ? (tabParam as AccountTab) : 'overview';

  useEffect(() => {
    if (tabParam && LEGACY_OVERVIEW_TABS.has(tabParam)) {
      setSearchParams({}, { replace: true });
    }
  }, [tabParam, setSearchParams]);
  const { snapshot } = usePortfolioWs(hasManualTradingOrigin());
  const [accounts, setAccounts] = useState<AccountInfoView[]>([]);
  const [summary, setSummary] = useState<AccountSummaryView | null>(null);
  const [dailyPnl, setDailyPnl] = useState<DailyAccountPnlView | null>(null);
  const [historyDays, setHistoryDays] = useState<AccountDailySummaryView[]>([]);
  const [selectedDayStart, setSelectedDayStart] = useState<number | null>(utcDayStartMs(Date.now()));
  const [calendarYear, setCalendarYear] = useState(() => new Date().getUTCFullYear());
  const [calendarMonth, setCalendarMonth] = useState(() => new Date().getUTCMonth());
  const [error, setError] = useState<string | null>(null);

  const todayStart = utcDayStartMs(Date.now());

  const loadAll = useCallback(async () => {
    if (!hasManualTradingOrigin()) return;
    setError(null);
    try {
      const monthStart = Date.UTC(calendarYear, calendarMonth, 1);
      const monthEnd = Date.UTC(calendarYear, calendarMonth + 1, 0);
      const threeMonthFrom = todayStart - 90 * MS_PER_DAY;
      const rangeFrom = Math.min(threeMonthFrom, monthStart - 90 * MS_PER_DAY);
      const rangeTo = Math.max(todayStart + MS_PER_DAY - 1, monthEnd);
      const [accRows, summaryRow, pnlRow, historyRow] = await Promise.all([
        listAccounts(),
        getAccountSummary(),
        getDailyAccountPnl(),
        getAccountDailySummaries({ fromMs: rangeFrom, toMs: rangeTo }),
      ]);
      setAccounts(accRows);
      setSummary(summaryRow);
      setDailyPnl(pnlRow);
      setHistoryDays(historyRow.days);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [calendarYear, calendarMonth]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!snapshot) return;
    setAccounts(snapshot.accounts);
    setDailyPnl(snapshot.daily_pnl);
    setSummary((prev) => {
      const total = snapshot.accounts.reduce((s, a) => s + a.total_usd, 0);
      const exchanges = snapshot.accounts.map((account) => ({
        exchange: account.exchange,
        total_usd: account.total_usd,
        im_rate: account.im_rate,
        mm_rate: account.mm_rate,
        share_ratio: total > 0 ? account.total_usd / total : 0,
        last_sync_at_ms: prev?.exchanges.find((e) => e.exchange === account.exchange)?.last_sync_at_ms ?? null,
      }));
      return {
        total_usd: total,
        exchange_count: snapshot.accounts.length,
        max_im_rate: Math.max(0, ...snapshot.accounts.map((a) => a.im_rate)),
        max_mm_rate: Math.max(0, ...snapshot.accounts.map((a) => a.mm_rate)),
        last_sync_at_ms: prev?.last_sync_at_ms ?? snapshot.ts_ms,
        daily_pnl: snapshot.daily_pnl.daily_pnl,
        daily_pnl_ratio: snapshot.daily_pnl.daily_pnl_ratio,
        exchanges,
      };
    });
  }, [snapshot]);

  const daysByStart = useMemo(() => {
    const map = new Map<number, AccountDailySummaryView>();
    for (const day of historyDays) {
      map.set(day.day_start_ms, day);
    }
    return map;
  }, [historyDays]);

  const recentHistoryDays = useMemo(() => {
    const cutoff = todayStart - 90 * MS_PER_DAY;
    return historyDays
      .filter((day) => day.day_start_ms >= cutoff)
      .slice()
      .sort((a, b) => a.day_start_ms - b.day_start_ms);
  }, [historyDays, todayStart]);

  const equityCurvePoints = useMemo(
    () => recentHistoryDays.map((d) => ({ t: d.day_start_ms, v: d.close_usd })),
    [recentHistoryDays],
  );

  const cumulativePnlPoints = useMemo(() => {
    let cumulative = 0;
    return recentHistoryDays.map((day) => {
      cumulative += day.daily_pnl;
      return { t: day.day_start_ms, v: cumulative };
    });
  }, [recentHistoryDays]);

  const selectedSummary = selectedDayStart != null ? daysByStart.get(selectedDayStart) : null;

  const onPrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarYear((y) => y - 1);
      setCalendarMonth(11);
    } else {
      setCalendarMonth((m) => m - 1);
    }
  };

  const onNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarYear((y) => y + 1);
      setCalendarMonth(0);
    } else {
      setCalendarMonth((m) => m + 1);
    }
  };

  const setActiveTab = (tab: AccountTab) => {
    if (tab === 'overview') {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  if (!hasManualTradingOrigin()) {
    return (
      <div className="account-overview-page">
        <p className="account-overview-error">
          未配置 trading-engine API（VITE_MANUAL_TRADING_ORIGIN）。请在环境变量中设置后刷新。
        </p>
      </div>
    );
  }

  const displaySummary = summary;
  const displayDailyPnl = dailyPnl;

  return (
    <div className="account-overview-page">
      <header className="account-overview-header">
        <div>
          <p className="account-overview-kicker">Portfolio Control</p>
          <h2>账户面板</h2>
        </div>
        <nav className="account-overview-tabs">
          {ACCOUNT_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </header>

      {error ? <p className="account-overview-error">{error}</p> : null}

      {activeTab === 'overview' ? (
        <>
          <div className="account-overview-summary-grid">
            <div className="account-overview-card">
              <p className="account-overview-card-label">总权益 (USD)</p>
              <p className="account-overview-card-value">{formatUsd(displaySummary?.total_usd ?? 0)}</p>
              <p className="account-overview-card-sub">
                {displaySummary?.exchange_count ?? 0} 个交易所
              </p>
            </div>
            <div className="account-overview-card">
              <p className="account-overview-card-label">今日收益</p>
              <p className={`account-overview-card-value ${pnlClass(displayDailyPnl?.daily_pnl)}`}>
                {formatUsd(displayDailyPnl?.daily_pnl ?? null)}
              </p>
              <p className={`account-overview-card-sub ${pnlClass(displayDailyPnl?.daily_pnl_ratio)}`}>
                {formatPct(displayDailyPnl?.daily_pnl_ratio ?? null)}
              </p>
            </div>
            <div className="account-overview-card">
              <p className="account-overview-card-label">最高 IM / MM</p>
              <p className="account-overview-card-value" style={{ fontSize: '1.1rem' }}>
                {formatRate(displaySummary?.max_im_rate ?? 0)} / {formatRate(displaySummary?.max_mm_rate ?? 0)}
              </p>
              <p className="account-overview-card-sub">各所保证金占用率峰值</p>
            </div>
            <div className="account-overview-card">
              <p className="account-overview-card-label">最近同步</p>
              <p className="account-overview-card-value" style={{ fontSize: '1rem' }}>
                {formatTs(displaySummary?.last_sync_at_ms)}
              </p>
              <p className="account-overview-card-sub">
                基准 {formatUsd(displayDailyPnl?.baseline_total_usd ?? null)}
              </p>
            </div>
          </div>

          <div className="account-overview-two-col account-overview-charts">
            <EquityLineChart title="近 90 日收盘权益" points={equityCurvePoints} height={300} color="#0f766e" />
            <EquityLineChart title="近 90 日累计收益" points={cumulativePnlPoints} height={300} color="#2563eb" />
          </div>

          <div className="account-overview-section">
            <h3>各交易所资产明细</h3>
            {accounts.length === 0 ? (
              <p className="account-overview-empty">暂无数据</p>
            ) : (
              <div className="account-overview-exchange-grid">
                {accounts
                  .slice()
                  .sort((a, b) => b.total_usd - a.total_usd)
                  .map((account) => {
                    const total = displaySummary?.total_usd ?? 1;
                    const share = total > 0 ? account.total_usd / total : 0;
                    const color = exchangeColor(account.exchange);
                    return (
                      <div key={account.exchange} className="account-overview-exchange-card">
                        <div className="account-overview-exchange-head">
                          <span className="account-overview-exchange-name">{account.exchange}</span>
                          <span className="account-overview-exchange-total">{formatUsd(account.total_usd)}</span>
                        </div>
                        <div className="account-overview-exchange-meta">
                          <span>IM {formatRate(account.im_rate)}</span>
                          <span>MM {formatRate(account.mm_rate)}</span>
                          <span>占比 {formatPct(share)}</span>
                        </div>
                        <div className="account-overview-share-bar">
                          <span style={{ width: `${share * 100}%`, background: color }} />
                        </div>
                        <table className="account-overview-asset-table">
                          <thead>
                            <tr>
                              <th>资产</th>
                              <th>权益</th>
                              <th>USD</th>
                            </tr>
                          </thead>
                          <tbody>
                            {account.assets.length === 0 ? (
                              <tr>
                                <td colSpan={3} style={{ color: '#9ca3af' }}>
                                  无明细
                                </td>
                              </tr>
                            ) : (
                              account.assets
                                .slice()
                                .sort((a, b) => b.usd_value - a.usd_value)
                                .map((asset) => (
                                  <tr key={asset.asset}>
                                    <td>{asset.asset}</td>
                                    <td>{asset.equity.toLocaleString('en-US', { maximumFractionDigits: 4 })}</td>
                                    <td>{formatUsd(asset.usd_value)}</td>
                                  </tr>
                                ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          <div className="account-overview-two-col">
            <PnlCalendar
              year={calendarYear}
              month={calendarMonth}
              daysByStart={daysByStart}
              selectedDayStart={selectedDayStart}
              onSelectDay={setSelectedDayStart}
              onPrevMonth={onPrevMonth}
              onNextMonth={onNextMonth}
            />

            <div className="account-overview-section account-overview-day-detail">
              {selectedSummary ? (
                <>
                  <h3>
                    选中日期 {new Date(selectedSummary.day_start_ms).toISOString().slice(0, 10)} 详情
                  </h3>
                  <div className="account-overview-day-stats">
                    <span>开盘 {formatUsd(selectedSummary.open_usd)}</span>
                    <span>收盘 {formatUsd(selectedSummary.close_usd)}</span>
                    <span className={pnlClass(selectedSummary.daily_pnl)}>
                      日收益 {formatUsd(selectedSummary.daily_pnl)} ({formatPct(selectedSummary.daily_pnl_ratio)})
                    </span>
                    <span>
                      高/低 {formatUsd(selectedSummary.high_usd)} / {formatUsd(selectedSummary.low_usd)}
                    </span>
                    <span>{selectedSummary.snapshot_count} 个快照</span>
                  </div>
                  {selectedSummary.close_accounts.length > 0 ? (
                    <div className="account-overview-allocation-list">
                      {selectedSummary.close_accounts.map((row) => (
                        <div key={row.exchange} className="account-overview-allocation-row">
                          <span>{row.exchange}</span>
                          <div className="account-overview-allocation-bar">
                            <span
                              style={{
                                width: `${selectedSummary.close_usd > 0 ? (row.total_usd / selectedSummary.close_usd) * 100 : 0}%`,
                                background: exchangeColor(row.exchange),
                              }}
                            />
                          </div>
                          <span style={{ textAlign: 'right' }}>{formatUsd(row.total_usd)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  <h3>日期详情</h3>
                  <p className="account-overview-empty">在日历中选择一天查看明细</p>
                </>
              )}
            </div>
          </div>
        </>
      ) : null}

      {activeTab === 'records' ? <HistoryRecordsPanel /> : null}

      {activeTab === 'transfers' ? <FundTransferSection onError={setError} /> : null}
    </div>
  );
}
